import { useState, useEffect, useMemo, useRef, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTasks, useProjects, useViews, useCreateTask } from '@/hooks/useTasks.ts';
import { parseCapture } from '@/lib/capture.ts';
import { formatDue } from '@/lib/dates.ts';

interface Item {
  id: string;
  section: string;
  label: string;
  hint?: string;
  run: () => void;
}

const NAV_ITEMS = [
  { label: 'Today', path: '/' },
  { label: 'Upcoming', path: '/upcoming' },
  { label: 'Inbox', path: '/inbox' },
  { label: 'All tasks', path: '/all' },
  { label: 'Table', path: '/table' },
  { label: 'Board', path: '/board' },
  { label: 'Search', path: '/search' },
  { label: 'Weekly review', path: '/review' },
];

/**
 * Cmd/Ctrl-K palette: jump to anything, or capture without leaving the keyboard.
 *
 * Task results come from the server's full-text index rather than filtering a
 * client-side copy, so it searches notes as well as titles and does not depend
 * on every task already being loaded.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const navigate = useNavigate();
  const createTask = useCreateTask();
  const { data: projects } = useProjects();
  const { data: views } = useViews();

  const deferred = useDeferredValue(query);
  const trimmed = deferred.trim();

  // Only hits the server once there is something to search for.
  const { data: taskResults } = useTasks(
    { q: trimmed, include_archived: true },
    undefined,
    { enabled: open && trimmed.length > 0 },
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Reset each time it opens, so it never reopens showing a stale search.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const lower = trimmed.toLowerCase();
    const out: Item[] = [];

    for (const task of (trimmed ? (taskResults ?? []) : []).slice(0, 8)) {
      out.push({
        id: `task:${task.id}`,
        section: 'Tasks',
        label: task.title,
        hint: task.due_at ? formatDue(task.due_at) : undefined,
        run: () => navigate(`/all?task=${task.id}`),
      });
    }

    for (const project of projects ?? []) {
      if (lower && !project.name.toLowerCase().includes(lower)) continue;
      out.push({
        id: `project:${project.id}`,
        section: 'Projects',
        label: project.name,
        hint: `${project.open_count} open`,
        run: () => navigate(`/project/${project.id}`),
      });
    }

    for (const view of views ?? []) {
      if (lower && !view.name.toLowerCase().includes(lower)) continue;
      out.push({
        id: `view:${view.id}`,
        section: 'Saved views',
        label: view.name,
        run: () => navigate(view.kind === 'board' ? `/board/${view.id}` : `/view/${view.id}`),
      });
    }

    for (const nav of NAV_ITEMS) {
      if (lower && !nav.label.toLowerCase().includes(lower)) continue;
      out.push({
        id: `nav:${nav.path}`,
        section: 'Go to',
        label: nav.label,
        run: () => navigate(nav.path),
      });
    }

    // Capture is always last so it never steals the default action from a
    // matching task, but is always reachable.
    if (trimmed) {
      const parsed = parseCapture(trimmed);
      if (parsed.title) {
        out.push({
          id: 'create',
          section: 'Create',
          label: `Add task: ${parsed.title}`,
          hint: parsed.dueAt ? formatDue(parsed.dueAt.toISOString()) : undefined,
          run: () =>
            createTask.mutate({
              title: parsed.title,
              status: 'inbox',
              priority: parsed.priority,
              due_at: parsed.dueAt?.toISOString() ?? null,
              tag_names: parsed.tags,
              ...(parsed.project ? { project_name: parsed.project } : {}),
            }),
        });
      }
    }

    return out;
  }, [trimmed, taskResults, projects, views, navigate, createTask]);

  // Clamp the cursor when the result list shrinks under it.
  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, items.length - 1)));
  }, [items.length]);

  if (!open) return null;

  function choose(index: number) {
    items[index]?.run();
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (items.length ? (a + 1) % items.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (items.length ? (a - 1 + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  let lastSection = '';

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-black/40"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className="fixed inset-x-0 top-[10vh] z-[70] mx-auto w-[92%] max-w-lg overflow-hidden rounded-xl"
        style={{ background: 'var(--color-page)', border: '1px solid var(--color-line)', boxShadow: '0 16px 48px rgba(0,0,0,0.35)' }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
          placeholder="Search tasks, jump to a view, or type to add…"
          aria-label="Command palette input"
          role="combobox"
          aria-expanded="true"
          aria-controls="tdl-palette-list"
          aria-activedescendant={items[active] ? `tdl-item-${items[active].id}` : undefined}
          className="w-full px-4 py-3 outline-none"
          style={{ background: 'transparent', color: 'var(--color-ink)', borderBottom: '1px solid var(--color-line)' }}
        />

        <ul id="tdl-palette-list" ref={listRef} role="listbox" className="max-h-80 overflow-y-auto py-1">
          {items.length === 0 && (
            <li className="px-4 py-3 text-sm" style={{ color: 'var(--color-ink-2)' }}>
              No matches.
            </li>
          )}

          {items.map((item, index) => {
            const header = item.section !== lastSection ? item.section : null;
            lastSection = item.section;

            return (
              <li key={item.id}>
                {header && (
                  <div
                    className="px-4 pt-2 pb-1 text-xs font-medium uppercase tracking-wide"
                    style={{ color: 'var(--color-ink-2)' }}
                  >
                    {header}
                  </div>
                )}
                <div
                  id={`tdl-item-${item.id}`}
                  role="option"
                  aria-selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => choose(index)}
                  className="flex cursor-pointer items-center gap-2 px-4 py-1.5 text-sm"
                  style={{
                    background: index === active ? 'var(--color-hover)' : 'transparent',
                    color: 'var(--color-ink)',
                  }}
                >
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.hint && (
                    <span className="shrink-0 text-xs" style={{ color: 'var(--color-ink-2)' }}>
                      {item.hint}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
}
